import { handleUnauthorized, refreshAccessToken } from "./api-client";

const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

/** Guard が「アクセストークン期限切れ」を示すために返すエラーコード（ApiClient と同じ）。 */
const TOKEN_EXPIRED_CODE = "TOKEN_EXPIRED";

/** 401 の本文が「期限切れ」かどうか。読めなければ期限切れとみなさない（安全側）。 */
async function isTokenExpired(res: Response): Promise<boolean> {
  try {
    const body = (await res.clone().json()) as { error?: { code?: string }; code?: string };
    return (body.error?.code ?? body.code) === TOKEN_EXPIRED_CODE;
  } catch {
    return false;
  }
}

/**
 * 認証必須のバイナリを取得する。
 *
 * **なぜ `apiClient` を使わないか**: `ApiClient` は JSON を前提にしており、
 * Blob を返せない（JSON 以外は `undefined` になる）。かといって素の `fetch` を直接書くと、
 * `ApiClient` が持っている 401 の扱い（期限切れなら refresh して 1 回だけ再試行、
 * 駄目ならログインへ誘導）に乗らない。その結果、管理画面を開きっぱなしにして
 * アクセストークンが切れると、本文や履歴（apiClient 経由）は黙って復帰するのに
 * **画像と添付だけ失敗し続け、しかもログインにも戻されない**。
 * ここに 1 箇所だけ実装して、ロゴ・添付プレビューの両方から使う。
 *
 * 相手のエンドポイントは 302 リダイレクトを返してはならない（`preferProxy: true` が必要）。
 * 理由は `components/feedback/file-preview.tsx` の `fetchAsBlob` を参照。
 */
export async function fetchBlobWithAuth(path: string): Promise<Blob> {
  const url = path.startsWith("http") ? path : `${baseUrl}${path}`;
  const send = (): Promise<Response> => fetch(url, { credentials: "include" });

  let res = await send();
  if (res.status === 401) {
    // 期限切れ以外の 401（そもそも未ログイン等）で refresh を試みても無駄なので分ける。
    if (await isTokenExpired(res)) {
      const refreshed = await refreshAccessToken();
      if (refreshed) res = await send();
    }
    // 更新できなかった / 更新しても 401 なら、セッションは切れている。
    // apiClient と同じくログインへ誘導する（画像だけ黙って失敗する状態にしない）。
    if (res.status === 401) {
      handleUnauthorized();
      throw new Error("セッションが切れました。再度ログインしてください");
    }
  }
  if (!res.ok) throw new Error(`取得に失敗しました (${res.status})`);
  return res.blob();
}
